import type {
  NotificationSchedule,
  NotificationSchedulePatch,
} from "../types/notifications";

/**
 * When the ERP speaks up on its own (E6, #446, slice 2): the morning summary's
 * time and the one global set of quiet hours.
 *
 * Pure and shared, like `audience.ts`: the API stores and enforces these, the
 * fixture adapter behaves the same, and `isQuietNow` is the one place anyone
 * asks "is it quiet?" — so the email and push slices read the same answer the
 * tests here pin down.
 */

/** The ticket's defaults. Quiet hours start switched off; the window is the suggested one. */
export const DEFAULT_NOTIFICATION_SCHEDULE: NotificationSchedule = {
  morningSummaryTime: "09:30",
  quietHours: { enabled: false, start: "21:00", end: "08:00" },
  timeZone: "Asia/Kolkata",
};

const CLOCK_TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isClockTime(value: unknown): value is string {
  return typeof value === "string" && CLOCK_TIME.test(value);
}

/** Minutes after midnight for an `HH:MM` the caller has already checked. */
export function minutesOfDay(time: string): number {
  const match = CLOCK_TIME.exec(time);
  if (!match) return 0;
  return Number(match[1]) * 60 + Number(match[2]);
}

export function isTimeZone(value: unknown): value is string {
  if (typeof value !== "string" || value === "") return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/** The calendar date (`YYYY-MM-DD`) and minute of the day at `now` in `timeZone`. */
export function localClock(
  now: Date,
  timeZone: string,
): { date: string; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const get = (type: string): string =>
    parts.find((part) => part.type === type)?.value ?? "00";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    minutes: Number(get("hour")) * 60 + Number(get("minute")),
  };
}

/**
 * Whether `now` falls inside the quiet hours. Off means never quiet. The window
 * runs from `start` up to but not including `end`, and when `end` is earlier
 * than `start` it crosses midnight (21:00 to 08:00 is quiet at 23:30 and at
 * 07:59, not at 08:00 or 12:00). Equal start and end is an empty window, not a
 * whole day.
 *
 * The bell is never affected by this: it is read by the channels that interrupt
 * a person — push and email — in later slices.
 */
export function isQuietNow(
  schedule: Pick<NotificationSchedule, "quietHours" | "timeZone">,
  now: Date,
): boolean {
  const { enabled, start, end } = schedule.quietHours;
  if (!enabled) return false;
  const from = minutesOfDay(start);
  const to = minutesOfDay(end);
  if (from === to) return false;
  const at = localClock(now, schedule.timeZone).minutes;
  return from < to ? at >= from && at < to : at >= from || at < to;
}

export type SchedulePatchResult =
  | { ok: true; patch: NotificationSchedulePatch }
  | { ok: false; fields: Record<string, string> };

/**
 * A request body as a patch: only the keys present change, and a key that is
 * present must be valid. A key this does not know is refused by name, because
 * ignoring a typo would answer "saved" for a change that was never made.
 */
export function parseSchedulePatch(body: unknown): SchedulePatchResult {
  if (typeof body !== "object" || body === null || Array.isArray(body)) {
    return { ok: false, fields: { body: "Send an object of settings." } };
  }
  const input = body as Record<string, unknown>;
  const patch: NotificationSchedulePatch = {};
  const fields: Record<string, string> = {};

  for (const key of Object.keys(input)) {
    if (!["morningSummaryTime", "quietHours", "timeZone"].includes(key)) {
      fields[key] = "Not a setting.";
    }
  }
  if (Object.keys(input).length === 0) {
    fields.body = "Send at least one setting to change.";
  }

  if ("morningSummaryTime" in input) {
    if (isClockTime(input.morningSummaryTime)) {
      patch.morningSummaryTime = input.morningSummaryTime;
    } else fields.morningSummaryTime = "A time like 09:30.";
  }

  if ("timeZone" in input) {
    if (isTimeZone(input.timeZone)) patch.timeZone = input.timeZone;
    else fields.timeZone = "Not a time zone, for example Asia/Kolkata.";
  }

  if ("quietHours" in input) {
    const quiet = input.quietHours;
    if (typeof quiet !== "object" || quiet === null || Array.isArray(quiet)) {
      fields.quietHours = "Send on, start and end.";
    } else {
      const next: NonNullable<NotificationSchedulePatch["quietHours"]> = {};
      for (const [key, value] of Object.entries(quiet)) {
        if (key === "enabled") {
          if (typeof value === "boolean") next.enabled = value;
          else fields["quietHours.enabled"] = "Yes or no.";
        } else if (key === "start" || key === "end") {
          if (isClockTime(value)) next[key] = value;
          else fields[`quietHours.${key}`] = "A time like 21:00.";
        } else {
          fields[`quietHours.${key}`] = "Not a setting.";
        }
      }
      const badInside = Object.keys(fields).some((key) =>
        key.startsWith("quietHours."),
      );
      if (Object.keys(next).length === 0 && !badInside) {
        fields.quietHours = "Send on, start or end.";
      }
      patch.quietHours = next;
    }
  }

  return Object.keys(fields).length > 0
    ? { ok: false, fields }
    : { ok: true, patch };
}

/** The schedule with a patch laid over it. */
export function applySchedulePatch(
  current: NotificationSchedule,
  patch: NotificationSchedulePatch,
): NotificationSchedule {
  return {
    morningSummaryTime: patch.morningSummaryTime ?? current.morningSummaryTime,
    quietHours: { ...current.quietHours, ...patch.quietHours },
    timeZone: patch.timeZone ?? current.timeZone,
  };
}

export function sameSchedule(
  a: NotificationSchedule,
  b: NotificationSchedule,
): boolean {
  return (
    a.morningSummaryTime === b.morningSummaryTime &&
    a.timeZone === b.timeZone &&
    a.quietHours.enabled === b.quietHours.enabled &&
    a.quietHours.start === b.quietHours.start &&
    a.quietHours.end === b.quietHours.end
  );
}

/** How many task references the summary names before saying "+N more". */
export const SUMMARY_NAMED_TASKS = 3;

/**
 * The line a morning summary carries after "Your tasks": counts, then the first
 * few tasks by reference. References, not titles — a title can carry a
 * customer's name, and this sentence is stored in a row that later slices send
 * through push and email.
 */
export function summaryDetail(input: {
  dueToday: number;
  overdue: number;
  references: readonly string[];
}): string {
  const counts: string[] = [];
  if (input.dueToday > 0) counts.push(`${String(input.dueToday)} due today`);
  if (input.overdue > 0) counts.push(`${String(input.overdue)} overdue`);
  const named = input.references.slice(0, SUMMARY_NAMED_TASKS);
  const more = input.dueToday + input.overdue - named.length;
  const list =
    named.length === 0
      ? ""
      : ` — ${named.join(", ")}${more > 0 ? ` +${String(more)} more` : ""}`;
  return `${counts.join(", ")}${list}`;
}
